import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Columns } from '@/components/landing/reusableSections/Columns';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	forBusiness_beauty_challenges,
	forBusiness_beauty_create,
	forBusiness_beauty_elevates,
	forBusiness_beauty_essential,
	forBusiness_beauty_perfect,
	forBusiness_beauty_tips,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import challenges from '@/public/images/for-business/beauty/challenges.webp';
import create from '@/public/images/for-business/beauty/create.webp';
import essential from '@/public/images/for-business/beauty/essential.webp';
import imgHero from '@/public/images/for-business/beauty/hero.webp';
import perfect from '@/public/images/for-business/beauty/perfect.webp';
import tips from '@/public/images/for-business/beauty/tips.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './BeautyProductsLanding.module.scss';

const BeautyProductsLanding: React.FC = () => {
	const { t } = useTranslationWithLocale('beauty-product-photography', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.BEAUTY_PRODUCTS_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.BEAUTY_PRODUCTS_LANDING },
	);

	useLandingShownEvent({
		type: LANDING_AMPLITUDE_EVENTS.BEAUTY_PRODUCTS_LANDING,
	});

	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['beauty__heroData-title'],
					mainSubtitle: styles['beauty__heroData-subtitle'],
				}}
			/>

			{/* challenges */}
			<ImageTextSection
				title={t('challengesData_mainTitle')}
				image={challenges}
				specialListItem
				data={forBusiness_beauty_challenges}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* essential */}
			<ImageTextSection
				title={t('essentialData_mainTitle')}
				subtitle1={t('essentialData_mainSubtitle1')}
				extraTextBelow={t('essentialData_extraText')}
				image={essential}
				data={forBusiness_beauty_essential}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['beauty__essential-image-container'],
					list: styles['beauty__essential-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* elevates */}
			<Columns
				t={t}
				title={t('elevateData_mainTitle')}
				data={forBusiness_beauty_elevates}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					columnTitle: styles['beauty__elevateData-title'],
				}}
			/>

			{/* howCreate */}
			<ImageTextSection
				title={t('createData_mainTitle')}
				image={create}
				data={forBusiness_beauty_create}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['beauty__howCreate-section'],
				}}
			/>

			{/* tips */}
			<ImageTextSection
				title={t('tipsData_mainTitle')}
				image={tips}
				data={forBusiness_beauty_tips}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['beauty__tips-image-container'],
					list: styles['beauty__tips-list'],
					section: styles['beauty__tips-section'],
				}}
			/>

			{/* perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfect}
				data={forBusiness_beauty_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['beauty__tryLift-title'],
					subtitleClass: styles['beauty__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default BeautyProductsLanding;
